
import { useState, useRef } from "react";
import { ImagePlus, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SingleImageUploaderProps {
  defaultImage?: string;
  onUpload?: (url: string) => Promise<void>;
  label?: string;
  size?: "sm" | "md" | "lg";
  folder?: string;
  userId?: string;
  onImageUpload?: (url: string) => void;
  imageUrl?: string;
  dimensions?: string;
  disabled?: boolean;
}

export function SingleImageUploader({ 
  defaultImage, 
  onUpload,
  label = "Upload Cover Image",
  size = "md",
  folder = "covers",
  userId,
  onImageUpload,
  imageUrl,
  dimensions,
  disabled = false
}: SingleImageUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [localImageUrl, setLocalImageUrl] = useState<string | null>(imageUrl || defaultImage || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sizes = {
    sm: "h-32 w-32",
    md: "h-48 w-48",
    lg: "h-64 w-64"
  };

  const handleUploadClick = () => {
    if (fileInputRef.current && !disabled) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      
      if (!event.target.files || event.target.files.length === 0) {
        return;
      }
      
      const file = event.target.files[0];
      
      // Check file size before uploading
      if (file.size > 5 * 1024 * 1024) { // 5MB
        toast.error("Arquivo muito grande. Tamanho máximo: 5MB");
        return;
      }
      
      // Compress if it's an image
      let fileToUpload = file;
      if (file.type.startsWith('image/')) {
        // Try to compress the image if it's too large
        if (file.size > 1 * 1024 * 1024) { // If > 1MB
          try {
            fileToUpload = await compressImage(file);
          } catch (error) {
            console.error("Error compressing image:", error);
            fileToUpload = file; // Use original if compression fails
          }
        }
      }
      
      const fileExt = file.name.split('.').pop();
      const filePath = `${folder}/${Math.random().toString(36).substring(2)}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('webtoons')
        .upload(filePath, fileToUpload, {
          cacheControl: '3600',
          upsert: false
        });
        
      if (uploadError) {
        throw uploadError;
      }
      
      const { data } = supabase.storage
        .from('webtoons')
        .getPublicUrl(filePath);
        
      const newImageUrl = data.publicUrl;
      setLocalImageUrl(newImageUrl);
      
      if (onUpload) {
        await onUpload(newImageUrl);
      }
      
      if (onImageUpload) {
        onImageUpload(newImageUrl);
      }
      
      toast.success("Image uploaded successfully");
    } catch (error) {
      toast.error("Error uploading image");
      console.error("Error uploading image:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    setLocalImageUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    if (onUpload) {
      await onUpload('');
    }
    
    if (onImageUpload) {
      onImageUpload('');
    }
  };

  // Image compression function
  const compressImage = (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Calculate the max dimensions to maintain aspect ratio
          const MAX_WIDTH = 1200;
          const MAX_HEIGHT = 1200;
          
          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);
          
          // Convert to webp for better compression if possible
          let quality = 0.8; // 80% quality
          const format = file.type === 'image/png' ? 'image/webp' : file.type;
          
          canvas.toBlob((blob) => {
            if (blob) {
              const newFile = new File([blob], file.name.split('.')[0] + (format === 'image/webp' ? '.webp' : '.' + file.type.split('/')[1]), {
                type: format
              });
              resolve(newFile);
            } else {
              reject(new Error('Canvas to Blob conversion failed'));
            }
          }, format, quality);
        };
        img.onerror = () => {
          reject(new Error('Image loading failed'));
        };
      };
      reader.onerror = () => {
        reject(new Error('FileReader failed'));
      };
    });
  };

  return (
    <div className="flex flex-col items-center space-y-2">
      <div 
        className={`relative flex ${sizes[size]} cursor-pointer items-center justify-center overflow-hidden rounded-md border border-dashed border-gray-300 bg-gray-50 ${!disabled ? 'hover:bg-gray-100' : ''}`}
        onClick={!disabled ? handleUploadClick : undefined}
        style={{ aspectRatio: size === 'sm' ? '1' : size === 'md' ? '1' : '3/4' }}
      >
        {localImageUrl ? (
          <>
            <img 
              src={localImageUrl} 
              alt="Uploaded image" 
              className="h-full w-full object-cover"
              width={size === 'sm' ? 128 : size === 'md' ? 192 : 256}
              height={size === 'sm' ? 128 : size === 'md' ? 192 : 256}
              loading="lazy"
            />
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
                className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
                aria-label="Remove image"
              >
                <X size={14} />
              </button>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center space-y-2 p-4 text-center">
            <ImagePlus className="h-8 w-8 text-gray-400" aria-hidden="true" />
            <span className="text-sm text-gray-500">{label}</span>
            {dimensions && <span className="text-xs text-gray-400">Recommended: {dimensions}</span>}
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
          </div>
        )}
      </div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        disabled={uploading || disabled}
        className="hidden"
        accept="image/*"
        aria-label={label}
      />
    </div>
  );
}
